import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Images, FolderOpen, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const Gallery = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchGalleryEvents();
  }, []);

  const fetchGalleryEvents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gallery/events`);
      setEvents(response.data);
    } catch (error) {
      // Gallery fetch failed - handled by empty state UI
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0A345E]">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white">Gallery</h1>
        </div>
      </section>

      {/* Gallery Events Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007A7A] mx-auto mb-4"></div>
              <p className="text-gray-500">Loading galleries...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16">
              <FolderOpen className="w-20 h-20 mx-auto text-gray-300 mb-6" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-3">No Galleries Yet</h2>
              <p className="text-gray-500 max-w-md mx-auto">
                We're working on capturing beautiful moments from our events. Check back soon!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-events-grid">
              {events.map((event) => (
                <Link to={`/gallery/${event.slug}`} key={event.id}>
                  <Card 
                    className="group h-full hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border-0 bg-[#F5F2E8]"
                    data-testid={`gallery-event-card-${event.slug}`}
                  >
                    {/* Cover Image */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      {event.cover_image ? (
                        <img
                          src={resolveImageUrl(event.cover_image)}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#007A7A]/20 to-[#0A345E]/20 flex items-center justify-center">
                          <Images className="w-16 h-16 text-[#007A7A]/40" />
                        </div>
                      )}
                      
                      {/* Photo Count Badge */}
                      <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                        <Images className="w-4 h-4" />
                        <span>{event.image_count || 0}</span>
                      </div>
                    </div>

                    {/* Event Info */}
                    <CardContent className="p-5">
                      <h3 className="text-lg font-semibold text-[#0A345E] mb-2 group-hover:text-[#007A7A] transition-colors duration-200">
                        {event.title}
                      </h3>
                      
                      {event.event_date && (
                        <div className="flex items-center text-sm text-gray-500 mb-3">
                          <Calendar className="w-4 h-4 mr-2" />
                          {formatDate(event.event_date)}
                        </div>
                      )}

                      {event.description && (
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">{event.description}</p>
                      )}

                      <div className="flex items-center text-[#007A7A] font-medium text-sm group-hover:translate-x-1 transition-transform duration-200">
                        View Gallery <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Gallery;
